import { Component, effect, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { finalize } from 'rxjs/operators';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PaneComponent } from '@rocksoft/cdk';
import { OSSupportedPipe } from '../../../../pipes/os-supported.pipe';
import { AppleMdmService } from '../../../../shared/services/apple-mdm-service';
import { AppleDevice } from '../../../../viewmodels/apple/apple-device.model';

@Component({
  selector: 'ml-mdm-erase-device',
  templateUrl: './erase-device.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [PaneComponent, ReactiveFormsModule, FaIconComponent, OSSupportedPipe],
})
export class EraseDeviceComponent {
  private readonly fb = inject(UntypedFormBuilder);
  private readonly appleMdmService = inject(AppleMdmService);
  private readonly modalService = inject(NgbModal);

  readonly apple = input.required<AppleDevice>();
  eraseDeviceForm: UntypedFormGroup;
  private osSupportedPipe = new OSSupportedPipe();

  constructor() {
    this.createForm();

    effect(() => {
      const apple = this.apple();

      this.eraseDeviceForm.reset({
        pin: 0,
        preserveDataPlan: false,
        disallowProximitySetup: false,
      });

      if (this.osSupportedPipe.transform(apple, { macOS: '>=10.8' })) {
        this.eraseDevicePin?.setValidators([Validators.required, Validators.minLength(6)]);
      } else {
        this.eraseDevicePin?.setValidators(null);
      }
    });
  }

  get eraseDevicePin(): AbstractControl | null {
    return this.eraseDeviceForm.get('eraseDevicePin');
  }

  get preserveDataPlan(): AbstractControl | null {
    return this.eraseDeviceForm.get('preserveDataPlan');
  }

  get getDisallowProximitySetup(): AbstractControl | null {
    return this.eraseDeviceForm.get('disallowProximitySetup');
  }

  public showEraseDeviceModal(content) {
    this.modalService.open(content);
  }

  onSubmit() {
    if (this.eraseDeviceForm.status !== 'VALID') {
      return;
    }

    this.eraseDeviceForm.disable();

    this.appleMdmService
      .eraseDevice(this.apple().udid, this.eraseDevicePin?.value, this.preserveDataPlan?.value)
      .pipe(
        finalize(() => {
          this.eraseDeviceForm.enable();
          this.eraseDeviceForm.markAsPristine();
        })
      )
      .subscribe({
        next: () => {
          this.modalService.dismissAll();
        },
      });
  }

  private createForm() {
    this.eraseDeviceForm = this.fb.group({
      eraseDevicePin: [0], // dynamic validation
      preserveDataPlan: [false],
      disallowProximitySetup: [false],
    });
  }
}
